// BlogPage.tsx
import React, { useEffect, useRef, useState } from 'react';
import {
    bigScreenWidth,
    fadeTimeout,
    getOverallPadding,
} from '../helpers/Themes';
import { Button, CardContent, Collapse, Fade, Typography } from '@mui/material';
import { useCustomTheme } from '../contexts/ThemeContext';
import { useApi } from '../contexts/ApiContext';
import { Blog, User } from '../workers/ApiWorker';
import { createWorkerFactory, useWorker } from '@shopify/react-web-worker';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import PageTopSection from '../components/universal/pageTopSection/PageTopSection';
import { isUserStaff } from '../helpers/Users';
import { useNavigate } from 'react-router-dom';
import { BlogCreateLink } from '../helpers/Links';
import BlogPreview from '../components/blog/BlogPreview';
import Breadcrumb from '../components/universal/breadcrumbs/breadcrumb';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

interface BlogPageProps {
    isMobile: boolean;
    padding?: number;
    user?: User;
}

const createApiWorker = createWorkerFactory(
    () => import('../workers/ApiWorker')
);

interface MonthGroup {
    label: string;
    blogs: Blog[];
}

const getMonthLabel = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
};

const groupBlogsByMonth = (blogs: Blog[]): MonthGroup[] => {
    const map = new Map<string, Blog[]>();
    for (const blog of blogs) {
        const label = getMonthLabel(blog.live ?? blog.createdAt);
        if (!map.has(label)) map.set(label, []);
        map.get(label)!.push(blog);
    }
    return Array.from(map.entries()).map(([label, blogs]) => ({
        label,
        blogs,
    }));
};

const NAVBAR_HEIGHT = 250;

const BlogPage: React.FC<BlogPageProps> = ({ isMobile, padding, user }) => {
    const { theme } = useCustomTheme();
    const { token } = useApi();
    const navigate = useNavigate();
    const apiWorker = useWorker(createApiWorker);
    const blogCreateLink = BlogCreateLink(isMobile);

    const [loadingResults, setLoadingResults] = useState<boolean>(true);
    const [loadingMore, setLoadingMore] = useState<boolean>(false);
    const [blogs, setBlogs] = useState<Blog[]>([]);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [lastPage, setLastPage] = useState<number>(1);
    const [expandedMonths, setExpandedMonths] = useState<Set<string>>(
        new Set()
    );

    const blogRefs = useRef<Map<string, HTMLDivElement>>(new Map());
    const monthGroups = groupBlogsByMonth(blogs);

    const sideColumnWidth = isMobile
        ? 0
        : window.innerWidth <= 1300
          ? 200
          : window.innerWidth > bigScreenWidth
            ? 700
            : 400;

    useEffect(() => {
        const fetchBlogs = async () => {
            try {
                setLoadingResults(true);
                const response = await apiWorker.getBlogs(token, 1);
                setBlogs(response.data);
                setCurrentPage(response.meta.page.current);
                setLastPage(response.meta.page.last);
            } catch (error) {
                console.error('Error fetching blogs:', error);
                setBlogs([]);
            } finally {
                setLoadingResults(false);
            }
        };
        fetchBlogs().catch(console.error);
    }, [apiWorker]);

    useEffect(() => {
        setExpandedMonths(new Set(monthGroups.map((g) => g.label)));
    }, [blogs]);

    const handleLoadMore = async () => {
        const nextPage = currentPage + 1;
        try {
            setLoadingMore(true);
            const response = await apiWorker.getBlogs(token, nextPage);
            setBlogs((prev) => [...prev, ...response.data]);
            setCurrentPage(response.meta.page.current);
            setLastPage(response.meta.page.last);
        } catch (error) {
            console.error('Error loading more blogs:', error);
        } finally {
            setLoadingMore(false);
        }
    };

    const scrollToBlog = (uuid: string) => {
        const el = blogRefs.current.get(uuid);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const toggleMonth = (label: string) => {
        setExpandedMonths((prev) => {
            const next = new Set(prev);
            if (next.has(label)) next.delete(label);
            else next.add(label);
            return next;
        });
    };

    const SidebarContent = () => (
        <Box sx={{ width: 'fit-content' }}>
            {!loadingResults &&
                blogs.length > 0 &&
                monthGroups.map((group) => {
                    const isExpanded = expandedMonths.has(group.label);
                    return (
                        <Box key={group.label} sx={{ mb: 0.5 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    py: 0.5,
                                    px: 1,
                                    borderRadius: 1,
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    onClick={() =>
                                        group.blogs.length > 0 &&
                                        scrollToBlog(group.blogs[0].uuid)
                                    }
                                    sx={{
                                        color: theme.palette.text.secondary,
                                        fontWeight: 600,
                                        fontSize: '0.8rem',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            color: theme.palette.primary.main,
                                        },
                                        transition: 'color 0.15s ease',
                                    }}
                                >
                                    {group.label}
                                </Typography>
                                <Box
                                    onClick={() => toggleMonth(group.label)}
                                    sx={{
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        borderRadius: 1,
                                        p: 0.3,
                                        ml: 1,
                                        '&:hover': {
                                            backgroundColor:
                                                theme.palette.action.hover,
                                        },
                                    }}
                                >
                                    {isExpanded ? (
                                        <ExpandLessIcon
                                            sx={{
                                                fontSize: 16,
                                                color: theme.palette.text
                                                    .disabled,
                                            }}
                                        />
                                    ) : (
                                        <ExpandMoreIcon
                                            sx={{
                                                fontSize: 16,
                                                color: theme.palette.text
                                                    .disabled,
                                            }}
                                        />
                                    )}
                                </Box>
                            </Box>

                            <Collapse in={isExpanded}>
                                <Box sx={{ pl: 1, pb: 0.5 }}>
                                    {group.blogs.map((blog) => (
                                        <Typography
                                            key={blog.uuid}
                                            variant="caption"
                                            onClick={() =>
                                                scrollToBlog(blog.uuid)
                                            }
                                            sx={{
                                                cursor: 'pointer',
                                                color: theme.palette.text
                                                    .disabled,
                                                py: 0.4,
                                                px: 1,
                                                borderRadius: 1,
                                                display: 'block',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                '&:hover': {
                                                    color: theme.palette.primary
                                                        .main,
                                                    backgroundColor:
                                                        theme.palette.action
                                                            .hover,
                                                },
                                                transition: 'color 0.15s ease',
                                            }}
                                        >
                                            {blog.name}
                                        </Typography>
                                    ))}
                                </Box>
                            </Collapse>
                        </Box>
                    );
                })}
        </Box>
    );

    return (
        <Fade in={true} timeout={fadeTimeout}>
            <Box sx={{ position: 'relative', overflow: 'hidden' }}>
                <PageTopSection
                    isMobile={isMobile}
                    padding={padding}
                    hideSeperator={true}
                />

                {/* Breadcrumb + create button */}
                <Box
                    sx={{
                        paddingLeft: getOverallPadding(isMobile, padding),
                        paddingRight: getOverallPadding(isMobile, padding),
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1,
                            overflow: 'auto',
                            justifyContent: isMobile ? 'center' : 'flex-start',
                        }}
                    >
                        <Breadcrumb
                            section={{ label: 'Blogs', path: '/blog' }}
                        />
                    </Box>

                    {user && isUserStaff(user) ? (
                        <Button
                            type="button"
                            variant="contained"
                            onClick={() => navigate(blogCreateLink.path)}
                            sx={{ padding: '0.8rem', mb: 2 }}
                        >
                            CREATE NEW BLOG
                        </Button>
                    ) : null}
                </Box>

                {/* 3-column layout */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        marginBottom: isMobile ? 10 : 3,
                    }}
                >
                    {/* Left column — placeholder to reserve space in layout */}
                    {!isMobile && (
                        <Box
                            sx={{
                                width: sideColumnWidth,
                                flexShrink: 0,
                            }}
                        >
                            {/* Fixed inner box — stays on screen while scrolling */}
                            <Box
                                sx={{
                                    position: 'fixed',
                                    top: NAVBAR_HEIGHT,
                                    width: sideColumnWidth,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    pt: 1,
                                    zIndex: 1,
                                }}
                            >
                                <SidebarContent />
                            </Box>
                        </Box>
                    )}

                    {/* Middle column — blog cards */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Card
                            sx={{
                                backgroundColor: 'transparent',
                                boxShadow: 'none',
                            }}
                        >
                            <CardContent
                                sx={{ backgroundColor: 'transparent' }}
                            >
                                <Fade in={true} timeout={fadeTimeout}>
                                    <div>
                                        <Typography
                                            variant="h2"
                                            sx={{ marginTop: 1 }}
                                        >
                                            Blogs
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{ marginTop: 1 }}
                                        >
                                            We post regularly and in detail
                                            about what we're doing
                                        </Typography>
                                    </div>
                                </Fade>
                            </CardContent>

                            {!loadingResults && blogs.length > 0 && (
                                <Fade in={true} timeout={fadeTimeout}>
                                    <div>
                                        <Box
                                            sx={{
                                                mt: 3,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                gap: 3,
                                                width: '100%',
                                            }}
                                        >
                                            {blogs.map((blog) => (
                                                <div
                                                    key={blog.uuid}
                                                    ref={(el) => {
                                                        if (el)
                                                            blogRefs.current.set(
                                                                blog.uuid,
                                                                el
                                                            );
                                                        else
                                                            blogRefs.current.delete(
                                                                blog.uuid
                                                            );
                                                    }}
                                                    style={{
                                                        width: '100%',
                                                        display: 'flex',
                                                        justifyContent:
                                                            'center',
                                                    }}
                                                >
                                                    <BlogPreview
                                                        image={blog.images.main}
                                                        name={blog.name}
                                                        miniDescription={
                                                            blog.miniDescription
                                                        }
                                                        date={blog.live}
                                                        clickable={true}
                                                        slug={blog.slug}
                                                    />
                                                </div>
                                            ))}

                                            {currentPage < lastPage && (
                                                <Button
                                                    variant="contained"
                                                    onClick={handleLoadMore}
                                                    disabled={loadingMore}
                                                    sx={{ marginTop: 2 }}
                                                >
                                                    {loadingMore
                                                        ? 'Loading...'
                                                        : 'Load More'}
                                                </Button>
                                            )}
                                        </Box>
                                    </div>
                                </Fade>
                            )}
                        </Card>
                    </Box>

                    {/* Right column — empty spacer, desktop only */}
                    {!isMobile && (
                        <Box
                            sx={{
                                width: sideColumnWidth,
                                flexShrink: 0,
                            }}
                        />
                    )}
                </Box>
            </Box>
        </Fade>
    );
};

export default BlogPage;
